import { useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import {
  HiOutlineArchiveBox,
  HiOutlineChevronRight,
  HiOutlineFolder,
  HiOutlineFolderOpen,
  HiOutlineFolderPlus,
  HiOutlineLockClosed,
  HiOutlineMagnifyingGlass,
} from "react-icons/hi2";

import {
  buildFolderTree,
  folderPathNames,
  indexFolders,
  type FileFolder,
  type FolderNode,
} from "../entities/files";
import { Notice } from "./Notice";

/** Подпись у папки, которая не принадлежит организации человека. */
export type FolderBadge = (folder: FileFolder) => string | null;

/**
 * Перетаскивание на рабочем столе: папку или файл несут мышью на папку дерева.
 *
 * Что именно несут и куда это можно опустить, знает страница: дерево только
 * рисует подсветку и сообщает, на какую папку отпустили (`null` — на «Все
 * файлы», то есть в корень).
 */
export interface FolderDnd {
  /** Что-то тащат прямо сейчас: подсвечиваются допустимые цели. */
  active: boolean;
  /** Идентификатор переносимой папки; `null`, если тащат файл или ничего. */
  draggingFolderId: string | null;
  draggableFolder: (folder: FileFolder) => boolean;
  canDrop: (targetId: string | null) => boolean;
  onDragStartFolder: (folder: FileFolder) => void;
  onDrop: (targetId: string | null) => void;
  onDragEnd: () => void;
}

/** Атрибуты строки дерева как цели перетаскивания. */
type RowDnd = (
  targetId: string | null,
  expand?: () => void,
) => Record<string, unknown>;

interface FolderTreePanelProps {
  /** Все доступные папки: по ним собирается дерево и читаются пути. */
  folders: readonly FileFolder[];
  /** Результат серверного поиска; `null`, пока запрос пуст. */
  found: readonly FileFolder[] | null;
  /** Почему не удался серверный поиск: пустой результат в таком случае был бы неправдой. */
  searchError: string | null;
  status: "loading" | "error" | "ready";
  /** Почему не загрузилось дерево; показывается при status = error. */
  errorReason: string | null;
  search: string;
  onSearch: (value: string) => void;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  badge: FolderBadge;
  /** Кнопка «Новая папка» есть, только когда создавать вообще можно. */
  onCreate: (() => void) | null;
  /** Подсказка над деревом: режим «Все организации» не даёт создавать папки. */
  hint: string | null;
  onRetry: () => void;
  dnd?: FolderDnd;
}

function Branch({
  nodes,
  depth,
  collapsed,
  toggle,
  selectedId,
  onSelect,
  badge,
  dnd,
  rowDnd,
}: {
  nodes: readonly FolderNode[];
  depth: number;
  collapsed: ReadonlySet<string>;
  toggle: (id: string) => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
  badge: FolderBadge;
  dnd: FolderDnd | undefined;
  rowDnd: RowDnd;
}) {
  return (
    <ul className="folder-tree__list">
      {nodes.map((node) => {
        const open = !collapsed.has(node.id);
        const selected = node.folder !== null && node.id === selectedId;
        const label = node.folder ? badge(node.folder) : null;

        return (
          <li key={node.id}>
            <div
              className="folder-tree__row"
              style={{ paddingInlineStart: `${depth * 0.9}rem` }}
              // Перетаскивается строка, а не кнопка внутри неё: Firefox не
              // начинает перетаскивание с самой кнопки.
              draggable={
                node.folder !== null && dnd?.draggableFolder(node.folder)
                  ? true
                  : undefined
              }
              data-dragging={
                dnd?.draggingFolderId === node.id ? "true" : undefined
              }
              onDragStart={(event) => {
                if (!node.folder || !dnd) return;
                event.dataTransfer?.setData("text/plain", node.name);
                if (event.dataTransfer)
                  event.dataTransfer.effectAllowed = "move";
                dnd.onDragStartFolder(node.folder);
              }}
              onDragEnd={dnd?.onDragEnd}
              {...(node.folder
                ? rowDnd(node.id, open ? undefined : () => toggle(node.id))
                : {})}
            >
              {node.children.length > 0 ? (
                <button
                  className="folder-tree__toggle"
                  type="button"
                  aria-expanded={open}
                  aria-label={`${open ? "Свернуть" : "Развернуть"} «${node.name}»`}
                  onClick={() => toggle(node.id)}
                >
                  <HiOutlineChevronRight aria-hidden="true" />
                </button>
              ) : (
                <span className="folder-tree__spacer" aria-hidden="true" />
              )}
              {node.folder ? (
                <button
                  className="folder-tree__item"
                  type="button"
                  aria-current={selected ? "true" : undefined}
                  onClick={() => onSelect(node.id)}
                >
                  {selected ? (
                    <HiOutlineFolderOpen aria-hidden="true" />
                  ) : (
                    <HiOutlineFolder aria-hidden="true" />
                  )}
                  <span className="folder-tree__name">{node.name}</span>
                  {label ? (
                    <span className="folder-tree__badge">{label}</span>
                  ) : null}
                </button>
              ) : (
                // Папку выше по дереву человеку не показали: выбрать её нельзя,
                // но вложенные папки под ней остаются на месте.
                <span
                  className="folder-tree__item folder-tree__item--stub"
                  title="Родительская папка принадлежит другой организации или недоступна вам"
                >
                  <HiOutlineLockClosed aria-hidden="true" />
                  <span className="folder-tree__name">{node.name}</span>
                </span>
              )}
            </div>
            {node.children.length > 0 && open ? (
              <Branch
                nodes={node.children}
                depth={depth + 1}
                collapsed={collapsed}
                toggle={toggle}
                selectedId={selectedId}
                onSelect={onSelect}
                badge={badge}
                dnd={dnd}
                rowDnd={rowDnd}
              />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Дерево папок слева от списка файлов.
 *
 * На узком экране дерево прячется за кнопкой с названием выбранной папки:
 * иначе оно заняло бы весь первый экран, а до самих файлов пришлось бы
 * долистывать.
 */
export function FolderTreePanel({
  folders,
  found,
  searchError,
  status,
  errorReason,
  search,
  onSearch,
  selectedId,
  onSelect,
  badge,
  onCreate,
  hint,
  onRetry,
  dnd,
}: FolderTreePanelProps) {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [openOnSmallScreen, setOpenOnSmallScreen] = useState(false);
  const tree = useMemo(() => buildFolderTree(folders), [folders]);
  const byId = useMemo(() => indexFolders(folders), [folders]);
  const selectedName = selectedId
    ? (byId.get(selectedId)?.name ?? "Папка")
    : "Все файлы";
  const searching = search.trim() !== "";

  const toggle = (id: string) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  const [over, setOver] = useState<string | null>(null);
  const expandTimer = useRef<number | null>(null);
  const stopExpand = () => {
    if (expandTimer.current !== null) window.clearTimeout(expandTimer.current);
    expandTimer.current = null;
  };
  // Отпустили или отменили перетаскивание — подсветка и таймер не должны остаться.
  const dragging = dnd?.active ?? false;
  useEffect(() => {
    if (dragging) return;
    setOver(null);
    stopExpand();
  }, [dragging]);
  useEffect(() => stopExpand, []);

  /**
   * Строка как цель. Сворачивание раскрывается, если задержаться над ней:
   * так папку внутри свёрнутой ветки достают, не бросая перетаскивание.
   */
  const rowDnd: RowDnd = (targetId, expand) => {
    if (!dnd || !dnd.active) return {};
    const ok = dnd.canDrop(targetId);
    const key = targetId ?? "root";

    return {
      "data-drop": ok ? "ok" : undefined,
      "data-over": ok && over === key ? "true" : undefined,
      onDragOver: (event: DragEvent) => {
        if (!ok) return;
        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
        setOver(key);
      },
      onDragEnter: (event: DragEvent) => {
        if (expand && expandTimer.current === null) {
          expandTimer.current = window.setTimeout(() => {
            expandTimer.current = null;
            expand();
          }, 700);
        }
        if (ok) event.preventDefault();
      },
      onDragLeave: (event: DragEvent) => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null))
          return;
        setOver((current) => (current === key ? null : current));
        stopExpand();
      },
      onDrop: (event: DragEvent) => {
        if (!ok) return;
        event.preventDefault();
        stopExpand();
        setOver(null);
        dnd.onDrop(targetId);
      },
    };
  };
  const choose = (id: string | null) => {
    onSelect(id);
    setOpenOnSmallScreen(false);
  };

  return (
    <aside className="folder-panel" aria-labelledby="folder-panel-title">
      <div className="folder-panel__head">
        <h2 id="folder-panel-title">Папки</h2>
        {onCreate ? (
          <button
            className="button button--secondary button--compact"
            type="button"
            onClick={onCreate}
          >
            <HiOutlineFolderPlus aria-hidden="true" />
            Новая папка
          </button>
        ) : null}
      </div>
      <button
        className="folder-panel__toggle"
        type="button"
        aria-expanded={openOnSmallScreen}
        aria-controls="folder-panel-body"
        onClick={() => setOpenOnSmallScreen((value) => !value)}
      >
        <span>
          <small>Открыта</small>
          <strong>{selectedName}</strong>
        </span>
        <HiOutlineChevronRight aria-hidden="true" />
      </button>
      <div
        id="folder-panel-body"
        className="folder-panel__body"
        // Ошибку загрузки прятать нельзя: без неё телефон показывает только «Все файлы».
        data-open={openOnSmallScreen || status === "error"}
      >
        <label className="folder-search">
          <span className="visually-hidden">Найти папку по названию</span>
          <HiOutlineMagnifyingGlass aria-hidden="true" />
          <input
            type="search"
            value={search}
            maxLength={255}
            placeholder="Найти папку"
            onChange={(event) => onSearch(event.target.value)}
          />
        </label>
        {hint ? <p className="folder-panel__hint">{hint}</p> : null}
        {dnd && status === "ready" ? (
          // Только там, где есть мышь: на телефоне перетаскивания нет, работают кнопки.
          <p className="muted folder-panel__note folder-panel__note--dnd">
            Файл или папку можно перетащить мышью на нужную папку в дереве.
          </p>
        ) : null}
        {onCreate === null && status === "ready" ? (
          // Без этой строки отсутствие кнопок выглядит как недоделка.
          <p className="muted folder-panel__note">
            Создавать и менять папки могут сотрудники с правом «Управление
            папками файлов».
          </p>
        ) : null}
        {status === "loading" ? (
          <p className="muted" aria-busy="true">
            Загружаем папки…
          </p>
        ) : null}
        {status === "error" ? (
          <Notice>
            <p>
              Не удалось загрузить папки: {errorReason ?? "неизвестная ошибка"}.
              Файлы доступны, но разложить их по папкам сейчас нельзя.
            </p>
            <button
              className="button button--text button--compact"
              type="button"
              onClick={onRetry}
            >
              Повторить
            </button>
          </Notice>
        ) : null}
        {status === "ready" ? (
          <nav className="folder-tree" aria-label="Дерево папок">
            <ul className="folder-tree__list">
              <li>
                <div className="folder-tree__row" {...rowDnd(null)}>
                  <span className="folder-tree__spacer" aria-hidden="true" />
                  <button
                    className="folder-tree__item"
                    type="button"
                    aria-current={selectedId === null ? "true" : undefined}
                    onClick={() => choose(null)}
                  >
                    <HiOutlineArchiveBox aria-hidden="true" />
                    <span className="folder-tree__name">Все файлы</span>
                  </button>
                </div>
              </li>
            </ul>
            {searching ? (
              searchError ? (
                <Notice>
                  Не удалось выполнить поиск: {searchError}. Дерево папок при
                  этом не изменилось.
                </Notice>
              ) : found && found.length > 0 ? (
                <ul className="folder-tree__list" aria-label="Найденные папки">
                  {found.map((folder) => {
                    const path = folderPathNames(byId, folder.id);
                    const label = badge(folder);

                    return (
                      <li key={folder.id}>
                        <div
                          className="folder-tree__row"
                          {...rowDnd(folder.id)}
                        >
                          <span
                            className="folder-tree__spacer"
                            aria-hidden="true"
                          />
                          <button
                            className="folder-tree__item folder-tree__item--found"
                            type="button"
                            aria-current={
                              folder.id === selectedId ? "true" : undefined
                            }
                            onClick={() => choose(folder.id)}
                          >
                            <HiOutlineFolder aria-hidden="true" />
                            <span className="folder-tree__name">
                              {folder.name}
                              {path.length > 1 ? (
                                <small>{path.slice(0, -1).join(" / ")}</small>
                              ) : null}
                            </span>
                            {label ? (
                              <span className="folder-tree__badge">
                                {label}
                              </span>
                            ) : null}
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : found ? (
                <p className="muted folder-tree__empty">
                  Папок с названием «{search.trim()}» нет.
                </p>
              ) : (
                <p className="muted" aria-busy="true">
                  Ищем…
                </p>
              )
            ) : tree.length > 0 ? (
              <Branch
                nodes={tree}
                depth={0}
                collapsed={collapsed}
                toggle={toggle}
                selectedId={selectedId}
                onSelect={choose}
                badge={badge}
                dnd={dnd}
                rowDnd={rowDnd}
              />
            ) : (
              <p className="muted folder-tree__empty">
                {onCreate
                  ? "Папок пока нет. Создайте первую кнопкой «Новая папка»: файлы останутся в общем списке, пока их не разложат."
                  : "Папок пока нет. Файлы лежат в общем списке."}
              </p>
            )}
          </nav>
        ) : null}
      </div>
    </aside>
  );
}
